/* eslint-disable @typescript-eslint/no-unused-vars */
import { MyCustomerApiResponse, NavCustomer } from "../../../store/services/customerService";
import { 
  format, 
  formatDistanceToNow, 
  isAfter, 
  subDays, 
  parseISO, 
  differenceInMilliseconds 
} from "date-fns";
import { sv } from "date-fns/locale";
import type { DeliveryEndFilter } from "./FilterButton";

/**
 * Format a date string into Swedish locale format
 */
export const formatDate = (dateString: string | null) => {
  if (!dateString) return null;
  const date = parseISO(dateString);
  return format(date, "yyyy-MM-dd HH:mm", { locale: sv });
};

/**
 * Check if a date is within the last 30 days
 */
export const isRecentDate = (dateString: string | null) => {
  if (!dateString) return false;
  const date = parseISO(dateString);
  const thirtyDaysAgo = subDays(new Date(), 30);
  return isAfter(date, thirtyDaysAgo);
};

/**
 * Check if a date is within the last N days
 */
export const isWithinDays = (dateString: string | null, days: number) => {
  if (!dateString) return false;
  const date = parseISO(dateString);
  const cutoff = subDays(new Date(), days);
  return isAfter(date, cutoff);
};

/**
 * Get the most recent login from a customer's WebLogins
 */
export const getMostRecentLogin = (customer: MyCustomerApiResponse) => {
  if (!customer.WebLogins || customer.WebLogins.length === 0) {
    return null;
  }
  
  return [...customer.WebLogins].sort((a, b) => 
    differenceInMilliseconds(parseISO(b.Time), parseISO(a.Time))
  )[0].Time;
};

/**
 * Format time relative to now in a readable format (e.g., "2 dagar sedan")
 */
export const formatTimeAgo = (dateString: string | null) => {
  if (!dateString) return null;
  
  try {
    return formatDistanceToNow(parseISO(dateString), {
      addSuffix: true,
      locale: sv
    });
  } catch (e) {
    return null;
  }
};

/**
 * Format a date with full details for tooltips
 */
export const formatFullDate = (dateString: string | null) => {
  if (!dateString) return null;
  
  try {
    return format(parseISO(dateString), "d MMM yyyy HH:mm", { locale: sv });
  } catch (e) {
    return null;
  }
};

/**
 * Compare login dates to determine a trend
 * Returns:
 * - "up" if there are multiple logins and frequency is increasing
 * - "down" if there are multiple logins and frequency is decreasing
 * - "neutral" if there's only one login or no clear trend
 * - null if no logins
 */
export const getLoginTrend = (customer: MyCustomerApiResponse): "up" | "down" | "neutral" | null => {
  if (!customer.WebLogins || customer.WebLogins.length === 0) {
    return null;
  }
  
  if (customer.WebLogins.length === 1) {
    return "neutral";
  }
  
  // Sort logins by date (newest first)
  const sortedLogins = [...customer.WebLogins].sort((a, b) => 
    differenceInMilliseconds(parseISO(b.Time), parseISO(a.Time))
  );
  
  // We need at least 3 logins to determine a trend
  if (sortedLogins.length < 3) {
    return "neutral";
  }
  
  // Calculate intervals between logins
  const intervals: number[] = [];
  for (let i = 0; i < sortedLogins.length - 1; i++) {
    const current = parseISO(sortedLogins[i].Time);
    const next = parseISO(sortedLogins[i + 1].Time);
    intervals.push(differenceInMilliseconds(current, next));
  }
  
  // Compare most recent interval with previous interval
  // If the most recent interval is shorter, frequency is increasing (trend up)
  // If the most recent interval is longer, frequency is decreasing (trend down)
  if (intervals[0] < intervals[1] * 0.8) {
    return "up"; // 20% shorter interval indicates increasing frequency
  } else if (intervals[0] > intervals[1] * 1.2) {
    return "down"; // 20% longer interval indicates decreasing frequency
  }
  
  return "neutral";
};
/**
 * Returnerar året om leveransslut-filtret täcker exakt ett helt kalenderår, annars null
 */
export const getDeliveryEndFilterYear = (filter: DeliveryEndFilter): number | null => {
  if (!filter?.from || !filter.to) return null;
  const year = filter.from.slice(0, 4);
  if (filter.from === `${year}-01-01` && filter.to === `${year}-12-31`) {
    return Number(year);
  }
  return null;
};

/**
 * Etikett för aktivt leveransslut-filter
 */
export const getDeliveryEndFilterLabel = (filter: DeliveryEndFilter): string => {
  if (!filter) return "";
  const year = getDeliveryEndFilterYear(filter);
  if (year !== null) return `Leveransslut ${year}`;
  if (filter.from && filter.to) return `Leveransslut ${filter.from} – ${filter.to}`;
  if (filter.from) return `Leveransslut från ${filter.from}`;
  if (filter.to) return `Leveransslut till ${filter.to}`;
  return "Leveransslut";
};

/**
 * Kontrollerar om kundens leveransslutdatum ligger inom filtrets intervall (inklusive gränser)
 */
export const matchesDeliveryEndFilter = (
  customer: MyCustomerApiResponse,
  filter: DeliveryEndFilter
): boolean => {
  if (!filter) return true;
  // Jämför som yyyy-MM-dd-strängar för att undvika tidszonsproblem
  const endDate = customer.CustomerDeliveryEndDate?.slice(0, 10);
  if (!endDate) return false;
  if (filter.from && endDate < filter.from) return false;
  if (filter.to && endDate > filter.to) return false;
  return true;
};
